import { useState, useCallback, useMemo } from 'react';
import {
  digits,
  getPortLifecycleCategory,
} from '../utils/portHelpers';
import {
  isServiceNumber,
  matchesConversionStatusFilter,
} from '../../../shared/portActivation';

const ITEMS_PER_PAGE = 10;
const UNKNOWN_OPERATOR = '__unknown__';
export const NO_BOX_FILTER = '__none__';

/** @typedef {'unused' | 'expiring' | 'dead' | null} LifecycleFilter */

/**
 * Hook for managing port search, filtering, and sorting
 */
export function usePortSearch(ports) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGatewayId, setSelectedGatewayId] = useState(null); // null = all gateways
  const [selectedSourceFilter, setSelectedSourceFilter] = useState(null); // null | gateway | service
  const [selectedConversionFilter, setSelectedConversionFilter] = useState(null); // null | requested | converted | not_converted
  const [selectedMobileOperator, setSelectedMobileOperator] = useState(null); // null = all
  const [sortByOldestMsg, setSortByOldestMsg] = useState(false);
  const [lifecycleFilter, setLifecycleFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const mobileOperatorOptions = useMemo(() => {
    const names = new Set();
    let hasUnknown = false;
    ports.forEach((port) => {
      const value = (port.mobile_operator || '').trim();
      if (value) names.add(value);
      else hasUnknown = true;
    });
    return {
      names: Array.from(names).sort((a, b) => a.localeCompare(b)),
      hasUnknown,
    };
  }, [ports]);

  const lifecycleCounts = useMemo(() => {
    const counts = { unused: 0, expiring: 0, dead: 0 };
    ports.forEach((port) => {
      const category = getPortLifecycleCategory(port);
      if (category !== 'healthy') counts[category] += 1;
    });
    return counts;
  }, [ports]);

  // Parse structured port query (e.g. "44G", "1A")
  const parsedStructuredPortQuery = useMemo(() => {
    const term = searchTerm.trim().toUpperCase();
    const m = term.match(/^(\d+)([A-H])$/); // e.g. "44G", "1A"
    if (!m) return null;
    const baseNum = m[1];
    const slotLetter = m[2];
    return { baseNum, slotLetter };
  }, [searchTerm]);

  // Filter ports based on search term and gateway
  const filteredPorts = useMemo(() => {
    let filtered = ports;

    // Filter by gateway first
    if (selectedGatewayId === NO_BOX_FILTER) {
      filtered = filtered.filter((port) => port.gateway_id == null || port.gateway_id === '');
    } else if (selectedGatewayId !== null) {
      filtered = filtered.filter((port) => {
        if (port.gateway_id) {
          return port.gateway_id === selectedGatewayId;
        }
        if (port.gateway && port.gateway.id) {
          return port.gateway.id === selectedGatewayId;
        }
        return false;
      });
    }

    if (selectedSourceFilter === 'service') {
      filtered = filtered.filter((port) => isServiceNumber(port));
    } else if (selectedSourceFilter === 'gateway') {
      filtered = filtered.filter((port) => !isServiceNumber(port));
    }

    if (selectedConversionFilter) {
      filtered = filtered.filter((port) =>
        matchesConversionStatusFilter(port, selectedConversionFilter)
      );
    }

    if (selectedMobileOperator !== null) {
      filtered = filtered.filter((port) => {
        const value = (port.mobile_operator || '').trim();
        if (selectedMobileOperator === UNKNOWN_OPERATOR) {
          return !value;
        }
        return value === selectedMobileOperator;
      });
    }

    // Special "assigned:false" shortcut
    if (searchTerm.trim().toLowerCase() === 'assigned:false') {
      return filtered.filter((p) => !p.assigned);
    }

    const trimmed = searchTerm.trim();

    // No search term — return everything (gateway filter already applied above)
    if (!trimmed) {
      return filtered;
    }

    const termLower = trimmed.toLowerCase();
    const termDigits = digits(trimmed);

    // Only match against phone digits when the ENTIRE term is digits.
    // e.g. "07349" → digit match ✅
    //      "KD1"   → digit match ❌ (would extract "1" and match almost every number)
    const termIsPureDigits = termDigits.length > 0 && /^\d+$/.test(trimmed);

    // Exact structured port-number match (e.g. "44G", "1A")
    if (parsedStructuredPortQuery) {
      const { baseNum, slotLetter } = parsedStructuredPortQuery;
      const target = `${baseNum}${slotLetter}`;
      return filtered.filter((p) => {
        const pn = String(p.port_number || '').toUpperCase();
        // Archived ports have suffix e.g. "12B_ARCHIVED_123" — match by prefix
        if (p.archived) return pn.startsWith(target + '_ARCHIVED_') || pn === target;
        return pn === target;
      });
    }

    return filtered.filter((port) => {
      const phoneDigits = digits(port.assigned_number);

      // Port number (e.g. "44G", "1A", or partial like "44")
      const matchesPortNumber =
        port.port_number != null &&
        String(port.port_number).toLowerCase().includes(termLower);

      // Phone number — full string match (e.g. "+447349...")
      const matchesAssigned =
        port.assigned_number != null &&
        String(port.assigned_number).toLowerCase().includes(termLower);

      // Phone number — digits-only match, ONLY when term is purely digits
      const matchesAssignedByDigits =
        termIsPureDigits &&
        phoneDigits.length > 0 &&
        phoneDigits.includes(termDigits);

      // User code (e.g. "KD1", "AB2")
      const matchesUserCode =
        port.user_code != null &&
        String(port.user_code).toLowerCase().includes(termLower);

      // Email address
      const matchesEmail =
        port.user_email != null &&
        String(port.user_email).toLowerCase().includes(termLower);

      // Slack user ID
      const matchesSlack =
        port.slack_user_id != null &&
        String(port.slack_user_id).toLowerCase().includes(termLower);

      // Mobile operator (e.g. "23430 EE EE")
      const matchesOperator =
        port.mobile_operator != null &&
        String(port.mobile_operator).toLowerCase().includes(termLower);

      const matchesSource =
        String(port.number_source || '').toLowerCase().includes(termLower) ||
        (termLower === 'service' && isServiceNumber(port)) ||
        (termLower === 'gateway' && !isServiceNumber(port));

      // Gateway name
      const matchesGatewayName =
        port.gateway != null &&
        port.gateway.name != null &&
        String(port.gateway.name).toLowerCase().includes(termLower);

      return (
        matchesPortNumber ||
        matchesAssigned ||
        matchesAssignedByDigits ||
        matchesUserCode ||
        matchesEmail ||
        matchesSlack ||
        matchesOperator ||
        matchesSource ||
        matchesGatewayName
      );
    });
  }, [ports, searchTerm, parsedStructuredPortQuery, selectedGatewayId, selectedMobileOperator, selectedSourceFilter, selectedConversionFilter]);

  const lifecycleFilteredPorts = useMemo(() => {
    if (!lifecycleFilter) return filteredPorts;
    return filteredPorts.filter(
      (port) => getPortLifecycleCategory(port) === lifecycleFilter
    );
  }, [filteredPorts, lifecycleFilter]);

  // Sort ports
  const sortedPorts = useMemo(() => {
    const list = [...lifecycleFilteredPorts];
    const sortOldest = sortByOldestMsg || lifecycleFilter != null;

    if (sortOldest) {
      return list.sort((a, b) => {
        const hasA = !!a.last_message_sent;
        const hasB = !!b.last_message_sent;
        if (!hasA && !hasB) return 0;
        if (!hasA) return 1;
        if (!hasB) return -1;
        const ta = new Date(a.last_message_sent).getTime();
        const tb = new Date(b.last_message_sent).getTime();
        return ta - tb; // older first; never-sent ports sink to bottom
      });
    }

    return list.sort((a, b) => {
      const ta = new Date(a.created_at).getTime() || 0;
      const tb = new Date(b.created_at).getTime() || 0;
      return ta - tb;
    });
  }, [lifecycleFilteredPorts, sortByOldestMsg, lifecycleFilter]);

  // Pagination
  const totalPages = useMemo(() => {
    return Math.ceil(sortedPorts.length / ITEMS_PER_PAGE) || 1;
  }, [sortedPorts.length]);

  const paginatedPorts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = currentPage * ITEMS_PER_PAGE;
    return sortedPorts.slice(start, end);
  }, [sortedPorts, currentPage]);

  // Search handlers
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setCurrentPage(1);
  }, []);

  const showUnassigned = useCallback(() => {
    setSearchTerm('assigned:false');
    setCurrentPage(1);
  }, []);

  const handleGatewayFilterChange = useCallback((gatewayId) => {
    if (gatewayId === '' || gatewayId == null) {
      setSelectedGatewayId(null);
    } else if (gatewayId === NO_BOX_FILTER) {
      setSelectedGatewayId(NO_BOX_FILTER);
    } else {
      setSelectedGatewayId(Number(gatewayId));
    }
    setCurrentPage(1);
  }, []);

  const handleSourceFilterChange = useCallback((source) => {
    setSelectedSourceFilter(source === '' ? null : source);
    setCurrentPage(1);
  }, []);

  const clearSourceFilter = useCallback(() => {
    setSelectedSourceFilter(null);
    setCurrentPage(1);
  }, []);

  const handleConversionFilterChange = useCallback((value) => {
    setSelectedConversionFilter(value === '' ? null : value);
    setCurrentPage(1);
  }, []);

  const clearConversionFilter = useCallback(() => {
    setSelectedConversionFilter(null);
    setCurrentPage(1);
  }, []);

  const clearGatewayFilter = useCallback(() => {
    setSelectedGatewayId(null);
    setCurrentPage(1);
  }, []);

  const handleMobileOperatorFilterChange = useCallback((operator) => {
    setSelectedMobileOperator(operator === '' ? null : operator);
    setCurrentPage(1);
  }, []);

  const clearMobileOperatorFilter = useCallback(() => {
    setSelectedMobileOperator(null);
    setCurrentPage(1);
  }, []);

  const toggleLifecycleFilter = useCallback((filter) => {
    setLifecycleFilter((prev) => (prev === filter ? null : filter));
    setCurrentPage(1);
  }, []);

  const clearLifecycleFilter = useCallback(() => {
    setLifecycleFilter(null);
    setCurrentPage(1);
  }, []);

  return {
    searchTerm,
    selectedGatewayId,
    selectedSourceFilter,
    selectedConversionFilter,
    selectedMobileOperator,
    mobileOperatorOptions,
    UNKNOWN_OPERATOR,
    sortByOldestMsg,
    setSortByOldestMsg,
    lifecycleFilter,
    lifecycleCounts,
    toggleLifecycleFilter,
    clearLifecycleFilter,
    currentPage,
    setCurrentPage,
    filteredPorts,
    sortedPorts,
    paginatedPorts,
    totalPages,
    handleSearchChange,
    clearSearch,
    showUnassigned,
    handleGatewayFilterChange,
    clearGatewayFilter,
    handleSourceFilterChange,
    clearSourceFilter,
    handleConversionFilterChange,
    clearConversionFilter,
    NO_BOX_FILTER,
    handleMobileOperatorFilterChange,
    clearMobileOperatorFilter,
    ITEMS_PER_PAGE,
  };
}


